import { Auth } from '@aws-amplify/auth';
import { Amplify } from '@aws-amplify/core';
import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { getStore } from './ApplicationState';
import {
  dataResidencyManager,
  DataResidencyCache,
  initializeAmplifyForDataResidency,
  resolvePersistedDataResidency,
  signInRoundRobin,
  SupportedDataResidency,
} from '@peekapak/data-residency';
import {
  CognitoUserProfileType,
  LoginState,
  expireSession,
  login,
  loginPartial,
  logout,
  reactivateSession,
  setUserProfile,
  startReactivation,
  updateField,
  UserType,
  LicenseLevel,
  CognitoUserProfileTypeAttributes,
} from './UserManagement';
import {
  getCleverOwnProfile,
  getCleverToken as gct,
  getUserProfileForMigration,
  resendNewPasswordConfirmation as rnpc,
  qrCodeSignIn as qrCodeSignInBackend,
} from './BackendInterface';
import { expireSessionAndRedirect } from './aaa';
import {
  getCleverProfile,
  getConfiguration,
  isTokenExpired,
  noWhiteSpace,
  setCookie,
} from './GlobalFunctions';
import { setUser, unsetUser } from './error-tracker';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
declare let gapi: Record<string, any>;
export type Provider = 'Google' | 'Clever' | 'CleverM' | 'ClassLink';
export type ConfirmationLinkState = 'EXPIRED' | 'OK' | 'STALE';

interface CleverProfile {
  data: {
    authorized_by: string;
    district: string;
    id: string;
    type: string;
  };
}

export function isElementaryStudent(profile: UserProfileType) {
  const { grade, type } = profile;
  const isStudent = type === 'Student';
  return isStudent && grade < 6;
}

export function getCleverToken(
  authorizationCode: string,
  redirectUri: string,
): Promise<GetCleverTokenResponse> {
  return new Promise((resolve, reject) => {
    gct(
      authorizationCode,
      redirectUri,
      (data) => {
        return resolve(data);
      },
      (error) => {
        return reject(error);
      },
    );
  });
}
export async function openHostedUIForGoogle() {
  return openHostedUI('Google');
}
export async function openHostedUIForClassLink() {
  return openHostedUI('ClassLink');
}
export async function openHostedUIForClever() {
  return openHostedUI('Clever');
}

export async function openHostedUIForCleverM() {
  return openHostedUI('CleverM');
}

async function setTokensManually(tokens: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  username: string; // Add username as a parameter
}): Promise<CognitoUser> {
  const { idToken, accessToken, refreshToken, username } = tokens;
  try {
    // CRITICAL: Use Auth's internal userPool to ensure proper storage configuration
    // @ts-ignore - accessing private property for session sharing
    const cognitoUserPool = Auth.userPool;

    if (!cognitoUserPool) {
      throw new Error(
        'Auth userPool not initialized. Ensure Amplify is configured.',
      );
    }

    // Create a new CognitoUser object
    const cognitoUser = new CognitoUser({
      Username: username, // Use the username or identifier of the user
      Pool: cognitoUserPool,
    });

    // Create token objects using CognitoIdToken, CognitoAccessToken, and CognitoRefreshToken
    const idTokenObject = new CognitoIdToken({ IdToken: idToken });
    const accessTokenObject = new CognitoAccessToken({
      AccessToken: accessToken,
    });
    const refreshTokenObject = new CognitoRefreshToken({
      RefreshToken: refreshToken,
    });

    // Create a CognitoUserSession object
    const userSession = new CognitoUserSession({
      IdToken: idTokenObject,
      AccessToken: accessTokenObject,
      RefreshToken: refreshTokenObject,
    });

    // Set the session to the user object
    cognitoUser.setSignInUserSession(userSession);

    // CRITICAL: Store the user in Amplify Auth's internal cache
    // Use the userPool's storage which is configured with cookies if initializeAmplifyForDataResidency set it up
    const storage = cognitoUserPool.storage;
    const clientId = cognitoUserPool.getClientId();
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

    // Store the tokens in the configured storage (cookies if configured)
    console.log(
      `🔑 Storing tokens with keyPrefix: ${keyPrefix}, username: ${username}`,
    );
    console.log(`🔑 Storage type:`, storage.constructor.name);

    storage.setItem(`${keyPrefix}.${username}.idToken`, idToken);
    storage.setItem(`${keyPrefix}.${username}.accessToken`, accessToken);
    storage.setItem(`${keyPrefix}.${username}.refreshToken`, refreshToken);
    storage.setItem(`${keyPrefix}.LastAuthUser`, username);

    // Also set the user in Auth's internal state
    // @ts-ignore - accessing private property
    Auth.user = cognitoUser;

    console.log(
      'Tokens set manually. User session initialized and stored in configured storage.',
    );

    return cognitoUser;
  } catch (error) {
    console.error('Error setting tokens manually:', error);
    throw error;
  }
}

export async function openHostedUI(provider: Provider) {
  try {
    // OAuth/SSO is only supported for US data residency
    // Always ensure we're using US configuration for OAuth
    console.log(
      `🔐 Initiating OAuth login for ${provider} - ensuring US data residency`,
    );

    // Check current data residency
    const currentDataResidency = dataResidencyManager.getCurrentDataResidency();

    if (currentDataResidency !== SupportedDataResidency.USA) {
      console.log(
        '⚠️ OAuth requested but not in US data residency - switching to US',
      );

      // Save current data residency to restore later if needed
      localStorage.setItem(
        'peekapak.preOAuthDataResidency',
        currentDataResidency,
      );

      // Switch to US data residency for OAuth (deliberate -> persisted)
      await dataResidencyManager.persistDataResidency(
        SupportedDataResidency.USA,
      );
      await initializeAmplifyForDataResidency(SupportedDataResidency.USA);

      console.log('✅ Switched to US data residency for OAuth login');
    } else {
      // Already in US data residency, but ensure Amplify is configured
      // This handles race conditions where OAuth is triggered before app initialization completes
      // The function has built-in guards to prevent redundant configuration
      await initializeAmplifyForDataResidency(SupportedDataResidency.USA);
    }

    // Check if OAuth is configured
    const authConfig = Auth.configure();
    if (!authConfig.oauth) {
      console.error('❌ OAuth not configured in US Cognito settings');
      throw new Error('OAuth configuration not available');
    }

    // Proceed with OAuth using US user pool
    Auth.federatedSignIn({
      provider,
    });
  } catch (error) {
    console.error(`Failed to open hosted UI for ${provider}:`, error);
    throw error;
  }
}

export function resendConfirmationCode(username: string) {
  return new Promise((resolve, reject) => {
    Auth.resendSignUp(username)
      .then((data) => {
        return resolve(data);
      })
      .catch((error) => {
        return reject(error);
      });
  });
}

export function checkConfirmationCode(username: string, code: string) {
  return new Promise((resolve, reject) => {
    Auth.confirmSignUp(username, code)
      .then((data) => {
        return resolve(data);
      })
      .catch((error) => {
        return reject(error);
      });
  });
}
export async function reactivateUserSession() {
  try {
    await reactivateUserSessionIfExists();
  } catch (error) {
    if (!isInnocuousError(error)) {
      throw error;
    }
  }
}

/** obtain the user's Cognito User Profile with a fresh session token
 */
export async function getRefreshedCognitoUser(): Promise<CognitoUserProfileType> {
  const session = await Auth.currentSession();

  // console.debug(
  //   `%cReactivating -  got current session`,
  //   'background: black; color: yellow'
  // );

  const cognitoUser = await Auth.currentAuthenticatedUser();

  cognitoUser.refreshSession(session.getRefreshToken(), () => {
    // console.debug(
    //   '%cRefreshed auth session',
    //   'background: black; color: yellow'
    // );
  });

  // console.debug(
  //   `%cReactivating - got cognito user, user session expires ${getReadableUnixTimeAnsi(
  //     expiryFromUser * 1000
  //   )} / ${expiryFromUser}, from session = ${expiryFromSession}`,
  //   'background: black; color: yellow'
  // );

  return cognitoUser;
}

/* eslint-disable complexity */
export async function reactivateUserSessionIfExists(): Promise<void> {
  const currentLoggedInState = getStore().getState().user.loginState;

  if (currentLoggedInState === LoginState.storybookAccess) {
    // console.debug(`%cParent storybook access detected`);
    return;
  }

  // Check if we're coming back from an OAuth redirect
  const isOAuthCallback =
    window.location.pathname === '/oauth/login' ||
    window.location.search.includes('code=') ||
    window.location.search.includes('error=');

  if (isOAuthCallback) {
    console.log(
      '🔐 OAuth callback detected - ensuring US data residency for OAuth',
    );
    // OAuth is US-only, ensure we're using US configuration
    if (
      dataResidencyManager.getCurrentDataResidency() !==
      SupportedDataResidency.USA
    ) {
      // OAuth is US-only (deliberate -> persisted)
      await dataResidencyManager.persistDataResidency(
        SupportedDataResidency.USA,
      );
      await initializeAmplifyForDataResidency(SupportedDataResidency.USA);
    } else {
      // Already in US data residency, but ensure Amplify is configured
      // This handles race conditions where OAuth callback happens before app initialization completes
      // The function has built-in guards to prevent redundant configuration
      await initializeAmplifyForDataResidency(SupportedDataResidency.USA);
    }
  }

  if (currentLoggedInState === LoginState.loggedIn) {
    const expiry =
      getStore().getState().user.cognitoProfile.signInUserSession.idToken
        .payload.exp;

    if (!isTokenExpired(expiry)) {
      return;
    }

    // console.debug(
    //   `%cToken is expired ...`,
    //   'background: blue; color: yellow',
    //   `time to expiry = `,
    //   Math.floor(Date.now() / 1000) - expiry
    // );
  }

  // console.debug(
  //   `%cReactivating - no longer logged in or too close to token expiry -> `,
  //   'background: black; color: yellow',
  //   currentLoggedInState
  // );

  if (
    currentLoggedInState === LoginState.reactivating ||
    currentLoggedInState === LoginState.authenticated
  ) {
    // console.debug(
    //   `%cReactivating - login state is reactivating, subscribing`,
    //   'background: black; color: yellow'
    // );
    return new Promise((resolve) => {
      const unsubscribe = getStore().subscribe(() => {
        const observedState = getStore().getState().user.loginState;

        if (observedState !== currentLoggedInState) {
          unsubscribe();
          return resolve();
        }
      });
    });
  }

  getStore().dispatch(startReactivation());

  // console.debug(
  //   `%cReactivating has started`,
  //   'background: black; color: yellow'
  // );

  try {
    let cognitoUser;

    if (isOAuthCallback) {
      // For OAuth callbacks, we know it's US-only, so skip multi-region attempts
      console.log('🔐 OAuth callback - using direct US reactivation');
      cognitoUser = await getRefreshedCognitoUser();
    } else {
      // Point Amplify at the account's region before asking Cognito for a
      // session. Resolved through the shared chokepoint, never by reading
      // localStorage here: that is what it takes to see the explicit
      // home-country choice AND migrate a residency value written by an older
      // build.
      //
      // This is the MYP-846 defect. A student signing in on peekapak.com lands
      // here via /play with the parent's 'ARE' already in storage; the old
      // hand-rolled read tested it against this build's own enum, did not
      // recognise it, logged "No stored data residency found", and forced USA -
      // so it went looking for the session under the US pool's cookie prefix
      // and found nothing. The student arrived logged out.
      try {
        const persistedDataResidency = resolvePersistedDataResidency();

        if (persistedDataResidency) {
          console.log(
            '🌍 Ensuring correct data residency for reactivation:',
            persistedDataResidency,
          );
          await initializeAmplifyForDataResidency(persistedDataResidency);
        } else {
          console.log(
            '⚠️ No stored data residency found, using current configuration',
          );
          // If no stored residency, try to initialize with default (US)
          await initializeAmplifyForDataResidency(SupportedDataResidency.USA);
        }
      } catch (error) {
        console.warn(
          '⚠️ Could not initialize data residency for reactivation:',
          error,
        );
        // Continue anyway - maybe the default region is correct
      }

      console.log('🔄 Attempting to get refreshed Cognito user');

      // Debug: Check what's in storage before trying to get the session
      try {
        // @ts-ignore
        const userPool = Auth.userPool;
        if (userPool) {
          const storage = userPool.storage;
          const clientId = userPool.getClientId();
          const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
          const lastAuthUser = storage.getItem(`${keyPrefix}.LastAuthUser`);
          console.log(`🔍 Debug - Storage type:`, storage.constructor.name);
          console.log(`🔍 Debug - LastAuthUser in storage:`, lastAuthUser);
          console.log(`🔍 Debug - Client ID:`, clientId);
          console.log(`🔍 Debug - Key prefix:`, keyPrefix);
          if (lastAuthUser) {
            const hasIdToken = !!storage.getItem(
              `${keyPrefix}.${lastAuthUser}.idToken`,
            );
            const hasAccessToken = !!storage.getItem(
              `${keyPrefix}.${lastAuthUser}.accessToken`,
            );
            const hasRefreshToken = !!storage.getItem(
              `${keyPrefix}.${lastAuthUser}.refreshToken`,
            );
            console.log(
              `🔍 Debug - Has tokens: idToken=${hasIdToken}, accessToken=${hasAccessToken}, refreshToken=${hasRefreshToken}`,
            );
          }
        }
      } catch (debugErr) {
        console.log('🔍 Debug error:', debugErr);
      }

      cognitoUser = await getRefreshedCognitoUser();
      console.log('✅ Got refreshed Cognito user:', cognitoUser?.getUsername());
    }

    if (nativeAccountAlreadyExists(cognitoUser)) {
      throw new Error(`User Pool account already exists`);
    }

    getStore().dispatch(reactivateSession(cognitoUser));

    let peekapakUserProfile;
    try {
      peekapakUserProfile = await getUserProfileFromDB();
    } catch (getUserProfileError) {
      getStore().dispatch(loginPartial());
      throw getUserProfileError;
    }

    // console.debug(
    //   `%cReactivating - got user profile ${peekapakUserProfile.userId}`,
    //   'background: black; color: yellow'
    // );
    getStore().dispatch(setUserProfile(peekapakUserProfile));
    setUser(peekapakUserProfile.userId);

    const identities = cognitoUser.signInUserSession.idToken.payload.identities;

    if (identities) {
      // console.debug(
      //   `%cReactivating - figuring out external identities`,
      //   'background: black; color: yellow'
      // );

      const googleIdentity = identities.filter((identity) => {
        return identity.providerName === 'Google';
      });

      if (googleIdentity.length > 0) {
        const googleUserId = googleIdentity[0].userId;
        // console.debug( `User has Google identity: ${ googleUserId }` );
        await initializeGoogleApiClients(googleUserId);
        getStore().dispatch(
          updateField({
            field: 'googleAccountUserId',
            value: googleUserId,
          }),
        );
      }

      const cleverIdentity = identities.filter((identity) => {
        // will match Clever and CleverM
        return identity.providerName.includes('Clever');
      });

      if (cleverIdentity.length > 0) {
        const cleverAccessToken =
          cognitoUser?.attributes?.['custom:pmAccessToken'];

        if (cleverAccessToken) {
          const cleverAccountUserId =
            cognitoUser?.attributes?.['custom:pmUserId'];

          if (!cleverAccountUserId)
            throw new Error(
              `Expected to find Clever User ID in account but did not`,
            );

          try {
            const cleverProfile = (await getCleverProfile(
              cleverAccountUserId,
              cleverAccessToken,
            )) as Record<string, unknown>;

            updateCleverCredentials(
              { data: { id: cleverAccountUserId } },
              cleverAccessToken,
              { data: cleverProfile },
            );
          } catch {
            // could not get clever profile so probably the access token has
            // expired
          }
        }
      }
    }

    // console.debug(
    //   `%cReactivating - figuring out Clever access`,
    //   'background: black; color: yellow'
    // );
    console.info(
      `User ${
        peekapakUserProfile?.userId || peekapakUserProfile?.email
      } session reactivated`,
    );
  } catch (err) {
    // Handle reactivation errors with the simpler approach
    if (!(err instanceof Error) && typeof err !== 'string') {
      throw err;
    }

    const errorMessage = (err as Error)?.message || err;
    console.log('❌ Session reactivation failed:', errorMessage);

    switch (errorMessage) {
      case 'No current user':
        getStore().dispatch(logout());
        break;

      case 'Refresh Token has expired':
        expireSessionAndRedirect(); // will dispatch expire session
        break;

      case 'Session reactivation failed in all data residency options':
        // Legacy case - kept for compatibility
        getStore().dispatch(logout());
        break;

      case 'Network error':
        console.warn(noWhiteSpace`Network error prevented reactivate user: ${errorMessage}, \
            possibly logged out. Current loginState === ${
              getStore().getState().user.loginState
            }`);
        break;

      case 'New Library user account created':
        console.debug(errorMessage);
        setCookie('peekapak.reloginIdp', 'true', 1);
        Auth.signOut();
        break;

      default:
        console.warn(noWhiteSpace`Cannot reactivate user: ${errorMessage}, \
            possibly logged out. Current loginState === ${
              getStore().getState().user.loginState
            }`);
        break;
    }

    throw err;
  }

  function nativeAccountAlreadyExists(data) {
    const { payload } = data.signInUserSession.idToken;
    return payload.isUserPoolAccountAlreadyExists;
  }
}
export function getUserProfileFromDB(): Promise<UserProfileType> {
  return new Promise((resolve, reject) => {
    getUserProfileForMigration(
      (response) => {
        return resolve(response);
      },
      (error) => {
        return reject(new Error(error.message));
      },
    );
  });
}
export function resendNewPasswordConfirmation(username: string): Promise<void> {
  return new Promise((resolve, reject) => {
    rnpc(
      username,
      () => {
        return resolve();
      },
      (error) => {
        return reject(error);
      },
    );
  });
}
export function forceChangePassword(
  username: string,
  temporaryPassword: string,
  newPassword: string,
): Promise<{
  email: string;
}> {
  return new Promise((resolve, reject) => {
    Auth.signIn(username, temporaryPassword)
      .then((user) => {
        if (
          !user.challengeName ||
          user.challengeName !== 'NEW_PASSWORD_REQUIRED'
        ) {
          return reject(
            new Error(
              `Did not receive expected new password required challenge`,
            ),
          );
        }

        return Auth.completeNewPassword(
          user,
          newPassword,
          user.challengeParam.requiredAttributes,
        );
      })
      .then((user) => {
        getStore().dispatch({
          type: 'LOGIN',
          payload: user,
        });
        return getUserProfileFromDB();
      })
      .then((userProfile) => {
        getStore().dispatch({
          type: 'SET_USER_PROFILE',
          payload: userProfile,
        });
        console.info(
          `Force change password succeeded for ${userProfile.email}`,
        );
        setUser(userProfile.userId);
        return resolve(userProfile);
      })
      .catch((error) => {
        return reject(error);
      });
  });
}
export function getNewPasswordConfirmationLinkState(
  username: string,
  temporaryPassword: string,
): Promise<ConfirmationLinkState> {
  return new Promise((resolve, reject) => {
    Auth.signIn(username, temporaryPassword)
      .then((user) => {
        if (
          !user.challengeName ||
          user.challengeName !== 'NEW_PASSWORD_REQUIRED'
        ) {
          return reject(
            new Error(
              `Did not receive expected new password required challenge`,
            ),
          );
        }

        return resolve('OK');
      })
      .catch((error) => {
        if (error.message.includes('Incorrect username or password')) {
          return resolve('STALE');
        } else if (
          error.message.includes(
            'User account has expired, it must be reset by an administrator.',
          )
        ) {
          return resolve('EXPIRED');
        } else if (error.message.includes('Temporary password has expired')) {
          return resolve('EXPIRED');
        }

        return reject(error);
      });
  });
}
/**
 * Sign a student in, trying each region in turn.
 *
 * Round-robin rather than the single-region resolution apps/frontend uses:
 * that one relies on the home-country control to correct a wrong guess, and an
 * elementary student cannot reasonably be asked which country their data lives
 * in. signInRoundRobin finds the account instead, then persists the region it
 * found so every later visit - and the handoff into the embedded /play build -
 * is a single attempt.
 *
 * There is deliberately no legacy fallback here any more. It used to catch any
 * error and retry Auth.signIn against whatever pool happened to be configured,
 * which swallowed the region-specific error and, now that every region is
 * already tried, would only re-send a known-bad password a third time.
 */
export async function signInUser(
  email: string,
  password: string,
): Promise<UserProfileType> {
  const result = await signInRoundRobin(email, password);

  getStore().dispatch(login(result.user));
  const profile = await getUserProfileFromDB();

  // userId, not email: this is children's PII and the id is what identifies a
  // user everywhere else (the error tracker included). Sibling call sites in
  // this file and in apps/frontend still log the email - a separate sweep.
  console.info(
    `${profile.userId} sign in successful in ${result.dataResidency} data residency`,
  );
  getStore().dispatch(setUserProfile(profile));
  setUser(profile.userId);
  return profile;
}

export async function qrCodeSignIn(qrCode: string) {
  // any exceptions that occur in the backend will be thrown back to the caller

  // IMPORTANT: Before calling the backend, ensure we have the current data residency
  const currentDataResidency = dataResidencyManager.getCurrentDataResidency();
  console.log(
    '🔐 QR code sign-in starting with data residency:',
    currentDataResidency,
  );

  // Call the backend - this might switch data residencies internally
  const response = await qrCodeSignInBackend(qrCode);

  // After the backend call, check if data residency changed
  const postCallDataResidency = dataResidencyManager.getCurrentDataResidency();
  console.log(
    '🔐 QR code sign-in backend returned, data residency is now:',
    postCallDataResidency,
  );

  // Initialize Amplify for the correct data residency BEFORE setting tokens
  await initializeAmplifyForDataResidency(postCallDataResidency);

  // Store the data residency for future reactivations
  localStorage.setItem('peekapak.userDataResidency', postCallDataResidency);
  console.log(
    '💾 Stored data residency for future reactivations:',
    postCallDataResidency,
  );

  // Update ONLY the mandatorySignIn setting without overwriting cookie configuration
  // We need to preserve the cookie settings from initializeAmplifyForDataResidency
  Amplify.configure({
    Auth: {
      mandatorySignIn: false,
    },
  });

  const account = await setTokensManually(response);

  if (!account) {
    throw new Error('Error setting tokens manually');
  }

  const cognitoUser = account as unknown as CognitoUserProfileType;
  cognitoUser.username = account.getUsername();
  cognitoUser.attributes = await fetchUserAttributes(account);

  getStore().dispatch(login(cognitoUser));

  // need to wait for login to be in effect before using
  const profile = await getUserProfileFromDB();

  console.info(`${profile.email || profile.userId} sign in successful`);
  getStore().dispatch(setUserProfile(profile));
  setUser(profile.userId);

  // Cache the successful data residency for this user
  if (profile.email) {
    DataResidencyCache.cacheSuccessfulLogin(
      profile.email,
      postCallDataResidency,
    );
  }

  return profile;

  async function fetchUserAttributes(
    cognitoUser: CognitoUser,
  ): Promise<CognitoUserProfileTypeAttributes> {
    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes(
        (
          err: Error | undefined,
          attributes: CognitoUserAttribute[] | undefined,
        ) => {
          if (err) {
            console.error('Error fetching attributes:', err);
            reject(err);
          } else {
            // if there is no error, then attributes exists
            const attrMap = attributes!.reduce(
              (acc: Record<string, string>, attr) => {
                acc[attr.getName()] = attr.getValue();
                return acc;
              },
              {},
            );
            resolve(attrMap as unknown as CognitoUserProfileTypeAttributes);
          }
        },
      );
    });
  }
}

export async function userChangePassword(
  oldPassword: string,
  newPassword: string,
) {
  const currentUser = await Auth.currentAuthenticatedUser();
  await Auth.changePassword(currentUser, oldPassword, newPassword);
  return true;
}
export async function initiateForgotPasswordFlow(username: string) {
  const data = await Auth.forgotPassword(username);
  return data;
}
export async function forgotPasswordSubmit(
  username: string,
  verificationCode: string,
  newPassword: string,
) {
  const data = await Auth.forgotPasswordSubmit(
    username,
    verificationCode,
    newPassword,
  );
  return data;
}
export async function signOutUser() {
  try {
    const loggedInUser = getStore().getState().user;
    const currentCognitoUser = await Auth.currentAuthenticatedUser();

    if (loggedInUser && loggedInUser.cognitoProfile) {
      console.info(`Logging out ${loggedInUser.cognitoProfile.username}...`);

      signOutFromLocalState();
      signOutOfCognito();
      return;
    } else if (currentCognitoUser) {
      signOutOfCognito();
      return;
    }
  } catch (error) {
    if (!isInnocuousError(error as Error)) {
      throw error;
    }

    return;
  }

  function signOutFromLocalState() {
    getStore().dispatch(logout());
    unsetUser();
  }

  function signOutOfCognito(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.signOut()
        .then(() => {
          return resolve();
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
}
export function isUserParentAccessStorybook(): boolean {
  return getStore().getState().user.loginState === LoginState.storybookAccess;
}
export function isUserDistrictAdministrator(): boolean {
  const userProfile = getStore().getState().user?.userProfile;
  if (!userProfile) return false;

  return (
    userProfile.userType === UserType.districtAdmin ||
    userProfile.licenseLevel === LicenseLevel.districtAdministrator
  );
}

export function isUserLoggedIn(): boolean {
  return getStore().getState().user.loginState === LoginState.loggedIn;
}
export function isUserLoggedInStateUnknown(): boolean {
  const currentState = getStore().getState().user.loginState;
  return currentState === LoginState.unknown;
}
export function isUserLoggedInStateInTransition(): boolean {
  const currentState = getStore().getState().user.loginState;
  return (
    currentState === LoginState.unknown ||
    currentState === LoginState.reactivating ||
    currentState === LoginState.authenticated
  );
}
export function isTokenUnavailable(): boolean {
  const currentState = getStore().getState().user.loginState;
  // console.debug(`isTokenUnavailable currentState = `, currentState);
  return (
    currentState === LoginState.unknown ||
    currentState === LoginState.reactivating
  );
}
export function waitUntilLoginState(
  timeout = 10000,
  isReadyFunction: (arg0: string) => boolean,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const intervalLength = 500;
    let intervalCount = 0;
    const intervalId = setInterval(checkState, intervalLength);

    function checkState() {
      const currentState = getStore().getState().user.loginState;
      // console.debug(
      //   `%ccurrentState `,
      //   'background: blue; color: yellow',
      //   currentState
      // );

      if (isReadyFunction.call(null, currentState)) {
        clearInterval(intervalId);
        return resolve();
      }

      intervalCount += 1;

      if (intervalLength * intervalCount > timeout) {
        clearInterval(intervalId);
        return reject(Error(`waitUntilUserLoginState Timeout expired`));
      }
    }
  });
}
export async function waitUntilTokenIsAvailable(
  timeout = 10000,
): Promise<void> {
  return await waitUntilLoginState(timeout, (currentState) => {
    return (
      currentState !== LoginState.unknown &&
      currentState !== LoginState.reactivating
    );
  });
}
export async function waitUntilUserLoggedInStateKnown(
  timeout = 20000,
): Promise<void> {
  return await waitUntilLoginState(timeout, (currentState) => {
    return (
      currentState !== LoginState.unknown &&
      currentState !== LoginState.authenticated &&
      currentState !== LoginState.reactivating
    );
  });
}
export function getRefreshTokenExpiry() {
  const initialAuthTime = getStore()
    .getState()
    .user.cognitoProfile.getSignInUserSession()
    .getIdToken().payload.auth_time;

  // the refresh token expiry is hardcoded and set in
  // the Cognito user pool app client configuration
  return initialAuthTime + 60 * 60 * 24;
}

export function isUserSessionExpired(): boolean {
  if (isUserLoggedIn()) {
    const expired = isTokenExpired(getRefreshTokenExpiry());
    if (expired) {
      getStore().dispatch(expireSession());
      return true;
    }

    return false;
  }

  console.warn(
    `User is not logged in but session expiry is being checked. This could be a logical error.`,
  );
  return false;
}

export function initializeGoogleApiClients(googleUserId = ''): Promise<void> {
  return new Promise((resolve) => {
    if (gapi && gapi.auth2 && gapi.client.classroom) {
      resolve();
    }

    const auth2Params = {
      client_id: getConfiguration('google').clientId,
      immediate: true,
      login_hint: '',
    };
    const driveClientParams = {
      discoveryDocs: getConfiguration('google').discoveryDocs,
      immediate: true,
      login_hint: '',
    };

    if (googleUserId) {
      auth2Params.login_hint = googleUserId;
      driveClientParams.login_hint = googleUserId;
    } else {
      delete auth2Params.login_hint;
      delete driveClientParams.login_hint;
    }

    gapi.load('client:auth2', () => {
      // console.debug( 'GAPI: client auth api loaded' );
      gapi.auth2.init(auth2Params).then(() => {
        // console.debug( 'GAPI: auth2 init done' );
        gapi.client.init(driveClientParams).then(() => {
          // console.debug( 'GAPI: classroom client init done' );
          resolve();
        });
      });
    });
  });
}
export function isGoogleUserSignedIn(): boolean {
  if (gapi && gapi.auth2) {
    return gapi.auth2.getAuthInstance().isSignedIn.get();
  }

  return false;
}
export async function isCleverUserSignedIn(): Promise<boolean> {
  const currentLoggedInState = getStore().getState().user.loginState;

  if (currentLoggedInState === LoginState.loggedIn) {
    const userProfile = getStore().getState().user.userProfile;
    if (userProfile) {
      // NB2: The below NB may not be true.....
      // NB: we do not need to check the expiry; the Clever access token would
      // have been obtained when the user logged in using Clever, so if the
      // user's Cognito refresh token is still valid, then the Clever access
      // token will be as well, since they both expire 24 hours after grant. If,
      // on the other hand, the user logged in using Cognito and then later
      // obtained a Clever access token by clicking on the + Sync using Clever
      // button, then the Clever access token will not expire before the Cognito
      // one does, which will prompt the user to re-login
      if (
        userProfile?.cleverAccessToken &&
        userProfile.cleverAccessToken.exp > Date.now()
      ) {
        return true;
      }

      // userProfile exists but no clever access token
      const cognitoProfile = getStore().getState().user.cognitoProfile;
      if (cognitoProfile?.attributes['custom:pmAccessToken']) {
        const at = cognitoProfile.attributes['custom:pmAccessToken'];

        try {
          const cleverProfile = (await getCleverOwnProfile(
            at,
          )) as CleverProfile;
          updateCleverCredentials(cleverProfile, at, cleverProfile, true);

          return true;
        } catch {
          return false;
        }
      }
    }

    return false;
  }

  return false;
}
export function getSignedInGoogleUserId(): void {
  if (gapi && gapi.auth2 && isGoogleUserSignedIn()) {
    return gapi.auth2.getAuthInstance().currentUser.get().getId();
  }

  throw new Error(`Google user not signed in`);
}
export async function promptUserGoogleSignIn(): Promise<void> {
  const { scope } = getConfiguration('google');

  if (gapi && gapi.auth2) {
    await gapi.auth2.getAuthInstance().signIn({
      prompt: 'select_account',
      scope,
    });
  } else {
    throw new Error(`Google API clients not initialized`);
  }
}
export async function signOutGoogleAccount() {
  if (gapi && gapi.auth2) {
    await gapi.auth2.getAuthInstance().signOut();
  } else {
    throw new Error(`Google API clients not initialized`);
  }
}
export function isGoogleApiInitialized() {
  const signedIn = isSignedIn();
  const classroomApiAccessible = !!gapi.client && !!gapi.client.classroom;
  return signedIn && classroomApiAccessible;

  function isSignedIn() {
    return (
      gapi &&
      gapi.auth2 &&
      gapi.auth2.getAuthInstance() &&
      gapi.auth2.getAuthInstance().isSignedIn.get()
    );
  }
}

function storeCleverCredentialsInProfile(
  cleverAccountUserId: string,
  cleverAccessToken: Record<string, unknown>,
  cleverProfile: Record<string, unknown>,
) {
  getStore().dispatch(
    updateField({ field: 'cleverAccountUserId', value: cleverAccountUserId }),
  );
  getStore().dispatch(
    updateField({ field: 'cleverAccessToken', value: cleverAccessToken }),
  );
  getStore().dispatch(
    updateField({ field: 'cleverProfile', value: cleverProfile }),
  );
}

export function updateCleverCredentials(
  cleverId: {
    data: {
      id: string;
    };
  },
  cleverAccessToken: string,
  profile: {
    data: Record<string, unknown>;
  },
  isExpiryUnknown?: boolean,
) {
  const cleverAccountUserId = cleverId.data.id;
  const cleverProfile = profile.data;
  const cleverAccessTokenWrapper = {
    token: cleverAccessToken,
    // NB: if we don't know the expiry, set the expiry to 10 minutes from the
    // present moment
    expiry: isExpiryUnknown
      ? Date.now() + 600 * 1000
      : Date.now() + 24 * 3600 * 1000,
  };
  storeCleverCredentialsInProfile(
    cleverAccountUserId,
    cleverAccessTokenWrapper,
    cleverProfile,
  );
}

export function isStudent(userProfile: UserProfileType | null) {
  return userProfile?.type === 'Student';
}

function isInnocuousError(error: unknown) {
  const message = (() => {
    if ((error as Error).message) return (error as Error).message;
    return error;
  })();

  switch (message) {
    case 'The user is not authenticated':
    case 'No current user':
      console.warn(`Innocuous error: ${message}`);
      return true;
    default:
      return false;
  }
}
